import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { useToast } from "../hooks/use-toast";
import { CONTACT } from "../mock";
import { ChevronRight, Phone, ClipboardPlus, HeartHandshake } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

export const Breadcrumbs = ({ items }) => (
  <nav className="text-[13px] text-white/80" aria-label="Breadcrumb">
    <ol className="flex flex-wrap items-center gap-1">
      {items.map((it, i) => (
        <li key={i} className="flex items-center gap-1">
          {it.to ? <Link to={it.to} className="hover:text-white underline-offset-2 hover:underline">{it.label}</Link> : <span className="text-white">{it.label}</span>}
          {i < items.length - 1 && <ChevronRight size={14} />}
        </li>
      ))}
    </ol>
  </nav>
);

export const PageHero = ({ title, subtitle, crumbs }) => (
  <section className="relative" style={{ background: "linear-gradient(120deg,#16357a 0%,#1b4f9c 100%)" }}>
    <div className="max-w-[1240px] mx-auto px-4 py-14 md:py-20">
      {crumbs && <div className="mb-4"><Breadcrumbs items={crumbs} /></div>}
      <h1 className="font-display text-white text-[34px] md:text-[46px] font-semibold leading-tight max-w-[820px]">{title}</h1>
      {subtitle && <p className="text-white/85 text-[17px] mt-4 max-w-[720px]">{subtitle}</p>}
    </div>
  </section>
);

export const CTABand = () => (
  <section className="bg-[#eef6ea] py-14">
    <div className="max-w-[1000px] mx-auto px-4 text-center">
      <h2 className="font-display text-[#16357a] text-[30px] md:text-[36px] font-semibold mb-3">Ready to Get Started?</h2>
      <p className="text-[#4a4a4a] text-[16px] mb-8 max-w-[640px] mx-auto">Request care, make a referral, or call our Columbus team. We're here to help patients, families, and healthcare professionals.</p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-[#4ca82f] hover:bg-[#3d8a26] text-white font-semibold px-7 py-3.5 rounded-full transition-colors"><HeartHandshake size={18} /> Request Care</Link>
        <Link to="/referral" className="inline-flex items-center justify-center gap-2 bg-[#16357a] hover:bg-[#0f2a5e] text-white font-semibold px-7 py-3.5 rounded-full transition-colors"><ClipboardPlus size={18} /> Make a Referral</Link>
        <a href={`tel:${CONTACT.phoneRaw}`} className="inline-flex items-center justify-center gap-2 border-2 border-[#16357a] text-[#16357a] hover:bg-[#16357a] hover:text-white font-semibold px-7 py-3.5 rounded-full transition-colors"><Phone size={18} /> {CONTACT.phone}</a>
      </div>
    </div>
  </section>
);

const fieldCls = "w-full border border-gray-200 focus:border-[#4ca82f] focus:ring-1 focus:ring-[#4ca82f] rounded-lg bg-white py-2.5 px-3 text-[14px] text-[#333] outline-none placeholder:text-gray-400";
const labelCls = "block text-[13px] font-semibold text-[#16357a] mb-1";

export const RequestCareForm = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", care: "", message: "" });
  const [consent, setConsent] = useState(false);
  const setE = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast({ title: "Missing information", description: "Please enter your name and email.", variant: "destructive" }); return; }
    if (!consent) { toast({ title: "Consent required", description: "Please accept the consent notice.", variant: "destructive" }); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { name: form.name, email: form.email, phone: form.phone, looking_for_care: "Yes", message: `Service needed: ${form.care}. ${form.message}`, consent });
      toast({ title: "Request received!", description: "Our CareFirst team will contact you shortly." });
      setForm({ name: "", email: "", phone: "", care: "", message: "" }); setConsent(false);
    } catch { toast({ title: "Something went wrong", description: `Please call ${CONTACT.phone}.`, variant: "destructive" }); }
    finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className={labelCls}>Name *</label><input className={fieldCls} placeholder="Full name" value={form.name} onChange={setE("name")} /></div>
        <div><label className={labelCls}>Email *</label><input type="email" className={fieldCls} placeholder="you@email.com" value={form.email} onChange={setE("email")} /></div>
        <div><label className={labelCls}>Phone</label><input className={fieldCls} placeholder="(614) 000-0000" value={form.phone} onChange={setE("phone")} /></div>
        <div><label className={labelCls}>Service Needed</label><input className={fieldCls} placeholder="e.g. Skilled Nursing" value={form.care} onChange={setE("care")} /></div>
      </div>
      <div><label className={labelCls}>How can we help?</label><Textarea rows={4} className="resize-none border-gray-200 focus-visible:ring-[#4ca82f]" placeholder="Tell us about your care needs" value={form.message} onChange={setE("message")} /></div>
      <label className="flex items-start gap-2 cursor-pointer">
        <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-1 data-[state=checked]:bg-[#4ca82f] data-[state=checked]:border-[#4ca82f]" />
        <span className="text-[12px] text-gray-500 leading-snug">I consent to be contacted by CareFirst Home Health regarding my request. *</span>
      </label>
      <Button type="submit" disabled={loading} className="w-full bg-[#4ca82f] hover:bg-[#3d8a26] text-white font-semibold py-6 rounded-full disabled:opacity-70">{loading ? "Submitting..." : "Request Care"}</Button>
    </form>
  );
};

export const ReferralForm = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({
    referrer_name: "", referrer_role: "", organization: "", referrer_phone: "", referrer_email: "",
    patient_name: "", patient_phone: "", program: "", services_needed: "", notes: "",
  });
  const [consent, setConsent] = useState(false);
  const setE = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.referrer_name || !f.referrer_email || !f.patient_name) { toast({ title: "Missing information", description: "Please complete the required fields.", variant: "destructive" }); return; }
    if (!consent) { toast({ title: "Consent required", description: "Please confirm you are authorized to share this information.", variant: "destructive" }); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/referral`, { ...f, consent });
      toast({ title: "Referral received!", description: "Our intake team will respond promptly." });
      setF({ referrer_name: "", referrer_role: "", organization: "", referrer_phone: "", referrer_email: "", patient_name: "", patient_phone: "", program: "", services_needed: "", notes: "" }); setConsent(false);
    } catch { toast({ title: "Something went wrong", description: `Please call or fax us at ${CONTACT.phone}.`, variant: "destructive" }); }
    finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <p className="text-[13px] font-bold text-[#16357a] uppercase tracking-wide">Referring Professional</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className={labelCls}>Your Name *</label><input className={fieldCls} value={f.referrer_name} onChange={setE("referrer_name")} placeholder="Full name" /></div>
        <div><label className={labelCls}>Role</label>
          <select className={fieldCls} value={f.referrer_role} onChange={setE("referrer_role")}>
            <option value="">Select role</option>
            {["Physician", "Hospital", "Discharge Planner", "Social Worker", "Case Manager", "Family Member", "Other Professional"].map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div><label className={labelCls}>Organization</label><input className={fieldCls} value={f.organization} onChange={setE("organization")} placeholder="Hospital / practice" /></div>
        <div><label className={labelCls}>Your Phone</label><input className={fieldCls} value={f.referrer_phone} onChange={setE("referrer_phone")} placeholder="(614) 000-0000" /></div>
        <div className="sm:col-span-2"><label className={labelCls}>Your Email *</label><input type="email" className={fieldCls} value={f.referrer_email} onChange={setE("referrer_email")} placeholder="you@organization.com" /></div>
      </div>
      <p className="text-[13px] font-bold text-[#16357a] uppercase tracking-wide pt-2">Patient Information</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className={labelCls}>Patient Name *</label><input className={fieldCls} value={f.patient_name} onChange={setE("patient_name")} placeholder="Patient full name" /></div>
        <div><label className={labelCls}>Patient Phone</label><input className={fieldCls} value={f.patient_phone} onChange={setE("patient_phone")} placeholder="(614) 000-0000" /></div>
        <div><label className={labelCls}>Insurance / Program</label>
          <select className={fieldCls} value={f.program} onChange={setE("program")}>
            <option value="">Select program</option>
            {["Medicare", "Medicaid", "PASSPORT", "DODD", "VA", "Private / Other"].map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div><label className={labelCls}>Services Needed</label><input className={fieldCls} value={f.services_needed} onChange={setE("services_needed")} placeholder="e.g. Skilled Nursing" /></div>
      </div>
      <div><label className={labelCls}>Notes / Clinical Details</label><Textarea rows={4} className="resize-none border-gray-200 focus-visible:ring-[#4ca82f]" value={f.notes} onChange={setE("notes")} placeholder="Diagnosis, discharge date, special instructions" /></div>
      <label className="flex items-start gap-2 cursor-pointer">
        <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-1 data-[state=checked]:bg-[#4ca82f] data-[state=checked]:border-[#4ca82f]" />
        <span className="text-[12px] text-gray-500 leading-snug">I confirm I am authorized to share this information for the purpose of a home health referral. *</span>
      </label>
      <Button type="submit" disabled={loading} className="w-full bg-[#16357a] hover:bg-[#0f2a5e] text-white font-semibold py-6 rounded-full disabled:opacity-70">{loading ? "Submitting..." : "Submit Referral"}</Button>
    </form>
  );
};
